import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { CategoryType, SettingsCategory } from '../types/categoriesList.types';

type Row = Record<string, any>;

const typeLabelMap: Record<string, string> = {
  work: 'Công việc',
  material: 'Vật tư',
  equipment: 'Thiết bị',
  personnel: 'Nhân sự',
  service: 'Dịch vụ',
  department: 'Phòng ban',
  other: 'Khác',
};

function mapCategoryRow(row: Row, groupMap: Map<string, any>, itemCount: number): SettingsCategory {
  // 1. Get group info from the pre-fetched map using group_id
  const group = row.group_id ? groupMap.get(row.group_id) : null;
  
  let groupCode = (group?.code || 'other') as CategoryType;
  
  // Fallback 1: Determine type from code prefix
  if (groupCode === 'other' && row.code) {
    const code = String(row.code).toUpperCase();
    if (code.startsWith('W-')) groupCode = 'work';
    else if (code.startsWith('M-')) groupCode = 'material';
    else if (code.startsWith('E-')) groupCode = 'equipment';
    else if (code.startsWith('P-')) groupCode = 'personnel';
    else if (code.startsWith('S-')) groupCode = 'service';
    else if (code.startsWith('DEPT-')) groupCode = 'department';
  }

  // Fallback 2: Determine type from keywords in name/code if still 'other'
  if (groupCode === 'other') {
    const name = String(row.name || '').toLowerCase();
    const code = String(row.code || '').toLowerCase();
    const combined = `${name} ${code}`;

    if (combined.includes('thi công') || combined.includes('công tác') || combined.includes('trồng') || combined.includes('lắp')) {
      groupCode = 'work';
    } else if (combined.includes('vật tư') || combined.includes('phân') || combined.includes('thuốc') || combined.includes('đất') || combined.includes('cỏ')) {
      groupCode = 'material';
    } else if (combined.includes('thiết bị') || combined.includes('máy') || combined.includes('công cụ') || combined.includes('dụng cụ') || combined.includes('đèn') || combined.includes('van')) {
      groupCode = 'equipment';
    } else if (combined.includes('nhân sự') || combined.includes('công nhân') || combined.includes('quản lý') || combined.includes('giám sát') || combined.includes('kế toán') || combined.includes('hành chính') || combined.includes('kỹ thuật')) {
      groupCode = 'personnel';
    } else if (combined.includes('dịch vụ') || combined.includes('hỗ trợ') || combined.includes('thuê')) {
      groupCode = 'service';
    } else if (combined.includes('phòng') || combined.includes('bộ phận') || combined.includes('kho') || combined.includes('tổ')) {
      groupCode = 'department';
    }
  }

  const iconConfig = row.icon_config || {};

  if (!group && groupCode === 'other' && row.group_id) {
    console.warn(`[CategoriesService] Category "${row.name}" has group_id (${row.group_id}) but it wasn't found in category_groups. Falling back to keyword mapping.`);
  }

  return {
    id: row.id,
    groupId: row.group_id,
    code: row.code || '---',
    name: row.name || 'Chưa có tên',
    description: row.description || '',
    type: groupCode,
    typeName: group?.name || typeLabelMap[groupCode] || 'Khác',
    itemCount,
    status: row.is_active ? 'active' : 'inactive',
    iconConfig: {
      name: iconConfig.name || 'ri-folder-line',
      tone: iconConfig.tone || 'bg-gray-100 text-gray-600',
    },
    parentId: row.parent_id,
    path: row.path,
    level: row.level || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const categoriesListService = {
  async getCategories(userId: string, companyId: string | null): Promise<SettingsCategory[]> {
    await permissionEngine.assert(userId, 'settings.categories.view', { companyId });
    
    const decision = await permissionEngine.can(userId, 'settings.categories.view', { companyId });
    const isAdmin = decision.reason === 'BYPASS_ADMIN';

    // 1. Fetch category groups first to build a lookup map
    let groupQuery = supabase
      .from('category_groups')
      .select('id, code, name');

    if (!isAdmin) {
      if (companyId) {
        groupQuery = groupQuery.or(`company_id.eq.${companyId},company_id.is.null`);
      } else {
        groupQuery = groupQuery.is('company_id', null);
      }
    }

    const { data: groups, error: groupsError } = await groupQuery;
    
    if (groupsError) {
      console.error('[CategoriesService] Error fetching category_groups:', groupsError);
    }

    if (!groups || groups.length === 0) {
      console.warn('[CategoriesService] No category_groups found in database. Check RLS policies or if data is inserted.');
    } else {
      console.log(`[CategoriesService] Loaded ${groups.length} category groups.`);
    }
    
    const groupMap = new Map<string, any>();
    groups?.forEach(g => groupMap.set(g.id, g));

    // 2. Fetch categories
    let query = supabase
      .from('categories')
      .select('*');

    if (!isAdmin) {
      if (companyId) {
        query = query.or(`company_id.eq.${companyId},company_id.is.null`);
      } else {
        query = query.is('company_id', null);
      }
    }

    const { data: categories, error: catError } = await query
      .order('path', { ascending: true })
      .order('display_order', { ascending: true });

    if (catError) {
      console.error('[CategoriesService] Error fetching categories:', catError);
      return [];
    }
    
    if (!categories || categories.length === 0) return [];

    // 3. Fetch item counts
    const categoryIds = categories.map((c) => c.id);
    const { data: services } = await supabase
      .from('services')
      .select('category_id')
      .in('category_id', categoryIds);

    const counts = (services || []).reduce<Record<string, number>>((acc, s) => {
      if (s.category_id) acc[s.category_id] = (acc[s.category_id] || 0) + 1;
      return acc;
    }, {});

    return categories.map((item) => mapCategoryRow(item, groupMap, counts[item.id] || 0));
  },

  async deleteCategory(id: string, userId: string, companyId: string): Promise<void> {
    await permissionEngine.assert(userId, 'settings.categories.manage', { companyId });
    
    // Get the path of the category to delete
    const { data: cat } = await supabase
      .from('categories')
      .select('path')
      .eq('id', id)
      .or(companyId ? `company_id.eq.${companyId},company_id.is.null` : 'company_id.is.null')
      .single();

    if (!cat?.path) {
      await supabase.from('categories').delete().eq('id', id).or(companyId ? `company_id.eq.${companyId},company_id.is.null` : 'company_id.is.null');
      return;
    }

    // Delete category and all its descendants using path
    const { error } = await supabase
      .from('categories')
      .delete()
      .or(companyId ? `company_id.eq.${companyId},company_id.is.null` : 'company_id.is.null')
      .or(`id.eq.${id},path.ilike.${cat.path}.%`);

    if (error) throw error;
  },
};
